import { Component, computed, input } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { TableRow } from "@bitwarden/web-vault/app/components/tables/transaction-table/transaction-table";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { Balances } from "@bitwarden/web-vault/app/models/enum/balance.enum";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { isStockOrOther } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";

export interface StatementBalanceData {
  source: string;
  balanceType: string;
  balance: number;
  date: string;
  description: string;
  symbol: string;
}

// For an account's very first balance, the ledger row can be a synthetic "starting_alignment"
// BAT (see BalanceAlignment.getOpeningBalanceOnFirstTransaction) generated for an opening,
// closing, or any other balance-defining transaction alike, so its own definition can't tell us
// which one it really is. BalanceAlignment records the actual raw transaction's definition on
// c_startingAlignmentSourceDefinition at creation time — read that directly instead of guessing,
// since the raw transaction isn't necessarily an opening/closing transaction itself (it can be a
// manual_balance transaction, or a regular transaction that merely has a bank imported balance
// set) and searching for it after the fact would risk matching an unrelated opening/closing
// transaction from a different statement period.
function resolveBalanceDefinition(transactionView: TransactionView): string {
  if (transactionView?.definition !== TransactionStatusEnum.starting_alignment) {
    return transactionView?.definition;
  }
  return transactionView.c_startingAlignmentSourceDefinition ?? transactionView.definition;
}

// A starting_alignment row is generated, never persisted, and the calculation store moves its date
// to the selected period's start. The Add Balance dialog only edits when it finds a stored balance
// with the same date and type, so "editing" this row would silently add a new balance instead.
export function canEditBalance(rowData: TableRow): boolean {
  return rowData?.transactionView?.definition !== TransactionStatusEnum.starting_alignment;
}

export function getStatementBalanceData(rowData: TableRow): StatementBalanceData {
  const transactionView = rowData.transactionView;
  const balanceOrigin = transactionView?.bankImportedBalanceOrigin;
  const balanceType = resolveBalanceDefinition(transactionView);

  let source = "Manual Input";
  if (balanceOrigin === Origin.basiq) {
    source = "Basiq — Bank Connection";
  } else if (balanceOrigin === Origin.plaid) {
    source = "Plaid — Bank Connection";
  }

  let balanceTypeLabel = "Balance";
  if (balanceType === TransactionStatusEnum.opening) {
    balanceTypeLabel = "Opening Balance";
  } else if (balanceType === TransactionStatusEnum.closing) {
    balanceTypeLabel = "Closing Balance";
  }

  return {
    source,
    balanceType: balanceTypeLabel,
    balance:
      transactionView?.bankImportedBalance ??
      transactionView?.quantity?.actualQuantity?.amount ??
      0,
    date: rowData.transactionDate,
    description: rowData.description || "",
    symbol:
      transactionView?.quantity?.actualQuantity?.symbol ??
      transactionView?.quantity?.currency ??
      rowData.symbol,
  };
}

@Component({
  templateUrl: "./statement-balance-details.component.html",
})
export class StatementBalanceDetailsComponent {
  rowData = input<TableRow>();
  parentDialogRef = input<MatDialogRef<any>>();
  private dialogFactory = new DialogFactory();

  constructor(public dialogRef: MatDialogRef<StatementBalanceDetailsComponent>) {}

  data = computed<StatementBalanceData>(() => {
    const rowData = this.rowData();
    if (!rowData) {
      return { source: "", balanceType: "", balance: 0, date: "", description: "", symbol: "" };
    }
    return getStatementBalanceData(rowData);
  });

  canEdit = computed<boolean>(() => !!this.rowData() && canEditBalance(this.rowData()));

  openEditBalance(): void {
    const rowData = this.rowData();
    if (!rowData || !canEditBalance(rowData)) {
      return;
    }

    const account = rowData.accountView;
    if (!account) {
      return;
    }

    const transactionView = rowData.transactionView;
    const initialBalance =
      transactionView?.bankImportedBalance ?? transactionView?.quantity?.actualQuantity?.amount;
    const initialDate = transactionView?.glossPostDate?.dateString || rowData.transactionDate;
    const initialCurrency = isStockOrOther(account.type)
      ? transactionView?.quantity?.actualQuantity?.symbol
      : undefined;
    const initialDescription = rowData.description;

    const resolvedDefinition = resolveBalanceDefinition(transactionView);
    const initialType =
      resolvedDefinition === TransactionStatusEnum.opening ? Balances.opening : Balances.closing;

    this.dialogFactory.addBalance(
      account,
      () => {
        this.dialogRef.close();
        this.parentDialogRef()?.close();
      },
      false,
      initialBalance,
      initialDate,
      initialCurrency,
      initialDescription,
      initialType,
    );
  }
}
